/* eslint-disable unicorn/no-null, sonarjs/no-nested-template-literals */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import type {
    TLocalCustomerLinkPayload,
    TRegistryCustomerLinkPayload
} from '@newo/api/gateway';

import { designerV2API } from '@/services/designer/designerV2Client';
import {
    finishProgressNotification,
    showProgressNotification
} from '@/utils/ui';

const CUSTOMER_LINK_QUERY_KEYS = {
    list: (projectIdn: string) => ['customer-links', projectIdn] as const,
    all: () => ['customer-links', 'all'] as const
};

interface ActivateLocalVersionVariables {
    projectIdn: string;
    projectVersionId: string;
}

interface UpsertRegistryCustomerLinkVariables {
    projectIdn: string;
    registryIdn: string;
    moduleIdn: string;
    moduleVersion?: string | null;
}

interface DeleteCustomerLinkVariables {
    customerLinkId: string;
    projectIdn: string;
}

const invalidateCustomerLinks = async (
    queryClient: QueryClient,
    projectIdn: string
): Promise<void> => {
    await Promise.all([
        queryClient.invalidateQueries({
            queryKey: CUSTOMER_LINK_QUERY_KEYS.list(projectIdn)
        }),
        queryClient.invalidateQueries({
            queryKey: CUSTOMER_LINK_QUERY_KEYS.all()
        })
    ]);
};

const upsertLocalCustomerLink = async ({
    projectIdn,
    projectVersionId
}: ActivateLocalVersionVariables) => {
    const link: TLocalCustomerLinkPayload = {
        type: 'local',
        data: { project_version_id: projectVersionId }
    };
    return designerV2API.upsertCustomerLink({
        project_idn: projectIdn,
        link
    });
};

const upsertRegistryCustomerLink = async ({
    projectIdn,
    registryIdn,
    moduleIdn,
    moduleVersion
}: UpsertRegistryCustomerLinkVariables) => {
    const link: TRegistryCustomerLinkPayload = {
        type: 'registry',
        data: {
            registry_idn: registryIdn,
            module_idn: moduleIdn,
            module_version: moduleVersion ?? null
        }
    };
    return designerV2API.upsertCustomerLink({
        project_idn: projectIdn,
        link
    });
};

export const useCustomerLinkQuery = (projectIdn?: string) =>
    useQuery({
        queryKey: CUSTOMER_LINK_QUERY_KEYS.list(projectIdn ?? ''),
        queryFn: async () => {
            const result = await designerV2API.listCustomerLinks({
                project_idn: projectIdn
            });
            return result.items.at(0) ?? null;
        },
        enabled: Boolean(projectIdn)
    });

export const useCustomerLinksQuery = (enabled = true) =>
    useQuery({
        queryKey: CUSTOMER_LINK_QUERY_KEYS.all(),
        queryFn: async () => {
            const result = await designerV2API.listCustomerLinks();
            return result.items;
        },
        enabled
    });

export const useActivateLocalVersionMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['customer-link', 'activate-local'],
        mutationFn: upsertLocalCustomerLink,
        onMutate: ({ projectVersionId }) => {
            const notificationId = `activate-local-${projectVersionId}`;
            showProgressNotification(
                notificationId,
                'Activating version',
                'Pointing runtime at the selected version…'
            );
            return { notificationId };
        },
        onSuccess: (_result, _variables, context) => {
            if (context.notificationId) {
                finishProgressNotification(
                    context.notificationId,
                    'green',
                    'Version activated'
                );
            }
        },
        onError: (_error, _variables, context) => {
            if (context?.notificationId) {
                finishProgressNotification(
                    context.notificationId,
                    'red',
                    'Could not activate version'
                );
            }
        },
        onSettled: (_data, _error, { projectIdn }) => {
            void invalidateCustomerLinks(queryClient, projectIdn);
        }
    });
};

export const useActivateRegistryModuleMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['customer-link', 'activate-registry'],
        mutationFn: upsertRegistryCustomerLink,
        onMutate: ({ moduleIdn, moduleVersion }) => {
            const notificationId = `activate-registry-${moduleIdn}-${moduleVersion ?? 'latest'}`;
            showProgressNotification(
                notificationId,
                'Activating module',
                `Pointing runtime at ${moduleIdn}${moduleVersion ? ` @ ${moduleVersion}` : ''}…`
            );
            return { notificationId };
        },
        onSuccess: (_result, _variables, context) => {
            if (context.notificationId) {
                finishProgressNotification(
                    context.notificationId,
                    'green',
                    'Module activated'
                );
            }
        },
        onError: (_error, _variables, context) => {
            if (context?.notificationId) {
                finishProgressNotification(
                    context.notificationId,
                    'red',
                    'Could not activate module'
                );
            }
        },
        onSettled: (_data, _error, { projectIdn }) => {
            void invalidateCustomerLinks(queryClient, projectIdn);
        }
    });
};

export const useUpsertCustomerLinkMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['customer-link', 'upsert', 'silent'],
        retry: 0,
        mutationFn: upsertRegistryCustomerLink,
        onSettled: (_data, _error, { projectIdn }) => {
            void invalidateCustomerLinks(queryClient, projectIdn);
        }
    });
};

export const useDeleteCustomerLinkMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['customer-link', 'delete', 'silent'],
        retry: 0,
        mutationFn: async ({ customerLinkId }: DeleteCustomerLinkVariables) =>
            designerV2API.deleteCustomerLink(customerLinkId),
        onSettled: (_data, _error, { projectIdn }) => {
            void invalidateCustomerLinks(queryClient, projectIdn);
        }
    });
};
